<template>
    <fragment>
        <c-generic-section>
            <div class="container-xs sf-top">
                <h2 class="mt-0">Sea Freight from China to Australia</h2>

                <p align="justify">
                    Sea freight remains the most cost-effective way to move commercial cargo from China to Australia — especially for furniture, machinery, building materials, e-commerce stock and any shipment where volume matters more than speed. Line-1 Logistics runs this lane end to end: we book the ocean freight, clear your goods through customs with our own ABF-licensed broker, and deliver from our 2,200 m&sup2; warehouse in Perth.
                </p>
                <p class="sf-zh">海运是中国到澳洲最经济的运输方式。一号线提供订舱、清关、仓储、派送全链路服务——清关由自有持牌报关员完成,不外包。</p>

                <div class="sf-badges">
                    <span class="sf-badge">ABF Licensed Customs Broker</span>
                    <span class="sf-badge">DAFF Accredited</span>
                    <span class="sf-badge">Own Warehouse in Perth</span>
                    <span class="sf-badge">中英双语服务</span>
                </div>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="seaVsAirHeader" class="center-content" />
                <div class="sf-cards">
                    <div class="sf-card">
                        <h4>Sea Freight 海运</h4>
                        <p align="justify">Best for large, heavy or non-urgent cargo. Significantly lower cost per cubic metre; typical port-to-port transit of two to three weeks. Full container (FCL) or shared container (LCL) options.</p>
                        <p class="sf-zh">适合大件、重货、不赶时间的货物,单方成本低;整柜/拼柜均可。</p>
                    </div>
                    <div class="sf-card">
                        <h4>Air Freight 空运</h4>
                        <p align="justify">Best for urgent, high-value or lightweight goods. Days instead of weeks, at a higher rate per kilogram. We run air freight too — track any air shipment on this site.</p>
                        <p class="sf-zh">适合急件、高值、轻货,时效以天计;空运单号可直接在本站查询轨迹。</p>
                    </div>
                </div>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="fclLclHeader" class="center-content" />
                <div class="sf-cards">
                    <div class="sf-card">
                        <h4>FCL &mdash; Full Container Load 整柜</h4>
                        <p align="justify">Your goods travel in their own sealed 20ft or 40ft container from the origin port to Australia. Fewer handling steps, lower risk of damage, and the most economical option once your cargo approaches 15 m&sup3; or more.</p>
                        <p class="sf-zh">独立整柜,装卸环节少、货损风险低;货量接近 15 立方以上通常整柜更划算。</p>
                    </div>
                    <div class="sf-card">
                        <h4>LCL &mdash; Less than Container Load 拼柜</h4>
                        <p align="justify">Your cargo shares a container with other shipments and is charged by volume. Ideal for smaller consignments — you only pay for the space you use. Slightly longer handling at both ends as the container is consolidated and unpacked.</p>
                        <p class="sf-zh">与其他货物拼柜,按体积计费,小批量首选;两端集拼/拆柜会略增时间。</p>
                    </div>
                </div>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="processHeader" class="center-content" />
                <ol class="sf-steps">
                    <li>
                        <h4>Quote &amp; Booking <span class="sf-step-zh">询价订舱</span></h4>
                        <p align="justify">Tell us what you are shipping, the volume and the supplier's location. We quote a flat rate up front and book space with the carrier.</p>
                    </li>
                    <li>
                        <h4>Export &amp; Documentation <span class="sf-step-zh">出口与单证</span></h4>
                        <p align="justify">Your supplier delivers to the origin port (or we arrange pickup). We check the commercial invoice, packing list and packing declaration before the vessel sails, so there are no surprises on arrival.</p>
                    </li>
                    <li>
                        <h4>Ocean Transit <span class="sf-step-zh">海上运输</span></h4>
                        <p align="justify">Typical port-to-port transit from major Chinese ports to Fremantle is around two to three weeks, subject to carrier schedules.</p>
                    </li>
                    <li>
                        <h4>Customs &amp; Quarantine <span class="sf-step-zh">清关与检疫</span></h4>
                        <p align="justify">Our in-house ABF-licensed broker prepares and lodges the Import Declaration, handles DAFF quarantine entries, and invoices any duty and GST before the goods are released.</p>
                    </li>
                    <li>
                        <h4>Warehouse &amp; Delivery <span class="sf-step-zh">仓储与派送</span></h4>
                        <p align="justify">Containers are unpacked at our Perth warehouse, with sideloader access and crane-truck delivery available for oversized cargo, then dispatched to your door.</p>
                    </li>
                </ol>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="costHeader" class="center-content" />
                <p align="justify">Sea freight rates move with the market, so we quote each shipment individually — flat, up front, with no hidden charges. The main factors that decide your rate:</p>
                <ul class="sf-list">
                    <li><b>Volume and weight</b> — LCL is charged per cubic metre; FCL is a fixed price per container.</li>
                    <li><b>Origin port</b> — direct sailings to Fremantle cost less than routes that tranship via another port.</li>
                    <li><b>Cargo type</b> — goods needing quarantine treatment, fumigation or special handling cost more to clear.</li>
                    <li><b>Season</b> — rates rise before Chinese New Year and in the pre-Christmas peak; booking early helps.</li>
                    <li><b>Delivery requirements</b> — door-to-door with unpacking, tailgate or crane truck adds to a port-to-port rate.</li>
                </ul>
                <p class="sf-zh">运价随市场浮动,我们对每票货一口价报价,无隐藏费用。影响价格的因素:货量与重量、起运港、货物类型(是否需检疫/熏蒸)、季节(年前与圣诞前为旺季)、派送要求。</p>
            </div>
        </c-generic-section>

        <c-generic-section top-divider>
            <div class="container-xs">
                <c-section-header :data="faqHeader" class="center-content" />
                <c-accordion>
                    <c-accordion-item title="How long does sea freight from China to Australia take?" active>
                        <p align="justify">Port-to-port transit from major Chinese ports (Shanghai, Ningbo, Shenzhen, Guangzhou) to Fremantle or the east-coast ports is typically two to three weeks, depending on the carrier and routing. Allow extra days for customs clearance, quarantine inspection if required, and delivery. 港到港通常 2–3 周,清关、检疫与派送需另计时间。</p>
                    </c-accordion-item>
                    <c-accordion-item title="Should I choose FCL or LCL?">
                        <p align="justify">As a rule of thumb, below roughly 15 m&sup3; LCL is usually cheaper; above that, a 20ft container of your own often costs about the same or less and is handled fewer times. Send us your packing list and we will price both. 一般 15 方以下拼柜更省,以上建议整柜;把装箱单发给我们,两种方案都可报价。</p>
                    </c-accordion-item>
                    <c-accordion-item title="What documents do I need?">
                        <p align="justify">A commercial invoice, packing list and packing declaration cover most shipments; some goods also need an import permit or treatment certificate. You can complete our standard declaration forms online at <a href="https://sea.line-1.com/forms/" target="_blank" rel="noopener">sea.line-1.com/forms</a>. 一般需要形式发票、装箱单和装箱申报,标准表格可在线填写。</p>
                    </c-accordion-item>
                    <c-accordion-item title="Will I pay duty and GST?">
                        <p align="justify">Commercial imports valued over AUD $1,000 are cleared on a formal Import Declaration (N10) with duty and GST assessed before release; many goods are duty-free under the China&ndash;Australia Free Trade Agreement (ChAFTA) with the right documentation. Our broker classifies your goods and applies any concession available. 货值超 AUD $1,000 需正式申报;凭中澳自贸协定产地证多数货物可免关税,GST 照常计征。</p>
                    </c-accordion-item>
                    <c-accordion-item title="What about quarantine (biosecurity)?">
                        <p align="justify">Australia has strict biosecurity rules. Timber packaging must be treated and declared, and goods like furniture, food-contact items and outdoor equipment may be inspected. As a DAFF-accredited operator we lodge quarantine entries and arrange any treatment required. 澳洲检疫严格:木质包装须熏蒸并申报;我们具备 DAFF 资质,代办检疫申报与处理。</p>
                    </c-accordion-item>
                    <c-accordion-item title="Can you deliver outside Perth?">
                        <p align="justify">Yes. Perth and Western Australia are our home base with our own warehouse and fleet access, and we arrange delivery to the east coast through our carrier network. 珀斯及西澳为自营主场,东岸城市通过合作网络派送。</p>
                    </c-accordion-item>
                </c-accordion>
            </div>
        </c-generic-section>

        <c-cta top-divider bottom-divider split />
    </fragment>
</template>

<script>
// import layout
import CLayout from '@/layouts/LayoutDefault.vue'
// import section header
import CSectionHeader from '@/components/sections/partials/SectionHeader.vue'
// import sections
import CGenericSection from '@/components/sections/GenericSection.vue'
import CCta from '@/components/sections/Cta.vue'
// import some required elements
import CAccordion from '@/components/elements/Accordion.vue'
import CAccordionItem from '@/components/elements/AccordionItem.vue'

export default {
  name: 'SeaFreight',
  components: {
    CSectionHeader,
    CGenericSection,
    CCta,
    CAccordion,
    CAccordionItem
  },
  data() {
    return {
      seaVsAirHeader: {
        title: 'Sea or Air?',
        paragraph: 'Two ways to ship — we run both, so our advice is not tied to one answer.'
      },
      fclLclHeader: {
        title: 'FCL vs LCL',
        paragraph: 'Whole container or shared space — priced both ways on request'
      },
      processHeader: {
        title: 'How It Works',
        paragraph: 'Five steps from your supplier\'s door to yours'
      },
      costHeader: {
        title: 'What Decides the Cost',
        paragraph: 'Flat quotes, no hidden charges'
      },
      faqHeader: {
        title: 'Sea Freight FAQ'
      }
    }
  },
  created() {
    this.$emit('update:layout', CLayout)
  }
}
</script>

<style scoped>
.sf-top h2 {
    margin-bottom: 16px;
}
.sf-zh {
    font-size: 13px;
    opacity: .7;
    margin-top: 6px;
}
.sf-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 24px;
}
.sf-badge {
    border: 1px solid rgba(29, 95, 194, .35);
    color: #1D5FC2;
    border-radius: 999px;
    padding: 4px 14px;
    font-size: 13px;
    white-space: nowrap;
}
.sf-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    text-align: left;
}
.sf-card {
    border: 1px solid rgba(128, 128, 128, .3);
    border-radius: 6px;
    padding: 20px 24px;
}
.sf-card h4 {
    margin-top: 0;
}
.sf-steps {
    list-style: none;
    counter-reset: sf-step;
    padding-left: 0;
    text-align: left;
}
.sf-steps > li {
    counter-increment: sf-step;
    position: relative;
    padding-left: 56px;
    margin-bottom: 28px;
}
.sf-steps > li::before {
    content: counter(sf-step);
    position: absolute;
    left: 0;
    top: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #1D5FC2;
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
}
.sf-steps h4 {
    margin: 0 0 8px;
}
.sf-steps p {
    margin: 0;
}
.sf-step-zh {
    font-size: 13px;
    opacity: .7;
    font-weight: 400;
    margin-left: 8px;
}
.sf-list {
    text-align: left;
}
.sf-list li {
    margin-bottom: 10px;
}
@media (max-width: 640px) {
    .sf-cards {
        grid-template-columns: 1fr;
    }
}
</style>
